// ============================================================================
// src/pig/breedCycleEditor.ts — the breeding-cycle editor's ordering rules
// ----------------------------------------------------------------------------
// BreedingView's cycle modal autosaves through the shared confirmed-draft
// controller. Three orderings decide whether that draft can be lost or turned
// into the wrong write; they live here, free of React, so they are unit-tested
// (CC5 review F12-F16):
//
//   * OPEN (F13): an unconfirmed draft is settled business. An opener that
//     finds one shows the modal over it and changes nothing else — loading
//     another cycle's form or edit id first would point the held draft at the
//     wrong cycle (or at none, which is a create).
//   * ROSTER (F14): an unstarted cycle re-pulls newly added breeders into its
//     roster when it opens. The roster the editor shows is the roster that is
//     saved: when it differs from the stored one it is queued as a draft, so
//     close (which flushes) persists it through breeding.update.
//   * DELETE (F12): a delete first settles the draft (saves any pending edit
//     and waits for an in-flight save), then runs breeding.delete. While the
//     delete is out, a save for that cycle is refused; once it is confirmed, a
//     leftover draft for it is dropped. A deleted cycle can never come back.
// ============================================================================

import type {PigBreedingCycle} from '../lib/pig.js';

export type BreedForm = {
  group: string;
  customSuffix: string;
  boar1Tags: string;
  boar2Tags: string;
  excludedSows: string[];
  exposureStart: string;
  notes: string;
  boar1Name: string;
  boar2Name: string;
};

/** Settles the modal for an opener; returns false when a held draft won. */
export function openCycleEditor(draft: {isDirty: () => boolean}, load: () => void, show: () => void): boolean {
  if (draft.isDirty()) {
    show();
    return false;
  }
  load();
  show();
  return true;
}

/** One tag per entry, whether stored comma- or newline-separated. */
export function rosterTags(text: unknown): string[] {
  return String(text ?? '')
    .split(/[\n,]/)
    .map((tag) => tag.trim())
    .filter(Boolean);
}

export function sameRoster(a: unknown, b: unknown): boolean {
  const left = rosterTags(a);
  const right = rosterTags(b);
  return left.length === right.length && left.every((tag, i) => tag === right[i]);
}

/**
 * The edit form for a saved cycle, and whether its shown roster differs from
 * the stored one (an unstarted cycle that picked up new breeders).
 */
export function cycleEditForm(
  cycle: PigBreedingCycle,
  opts: {
    locked: boolean;
    mergeRoster: (group: string, boar1: string, boar2: string, excluded: string[]) => string;
    boarNames: {boar1: string; boar2: string};
  },
): {form: BreedForm; rosterChanged: boolean} {
  const baseB1 = (cycle.boar1Tags || '').split(',').join('\n').split(', ').join('\n');
  const baseB2 = (cycle.boar2Tags || '').split(',').join('\n').split(', ').join('\n');
  const excluded = Array.isArray(cycle.excludedSows) ? (cycle.excludedSows as string[]) : [];
  // Unstarted cycles re-pull newly added sows from the breeder registry,
  // skipping ones previously removed (excludedSows). Started cycles are
  // frozen — show what was saved.
  const liveB1 = opts.locked ? baseB1 : opts.mergeRoster(cycle.group as string, baseB1, baseB2, excluded);
  return {
    form: {
      group: cycle.group as string,
      customSuffix: cycle.customSuffix || '',
      boar1Tags: liveB1,
      boar2Tags: baseB2,
      excludedSows: excluded,
      exposureStart: cycle.exposureStart as string,
      notes: (cycle.notes as string) || '',
      boar1Name: cycle.boar1Name || opts.boarNames.boar1,
      boar2Name: cycle.boar2Name || opts.boarNames.boar2,
    },
    rosterChanged: !sameRoster(cycle.boar1Tags, liveB1),
  };
}

export type CycleDeleteGate = {id: string; phase: 'settling' | 'deleting' | 'deleted'} | null;

/**
 * What a save for `cycleId` may do while a delete is about: persist it, refuse
 * it (the delete is out — the draft is held), or drop it (the cycle is gone).
 */
export function cycleSaveVerdict(gate: CycleDeleteGate, cycleId: string | null): 'persist' | 'refuse' | 'drop' {
  if (!gate || !cycleId || gate.id !== cycleId || gate.phase === 'settling') return 'persist';
  return gate.phase === 'deleting' ? 'refuse' : 'drop';
}

/** A delete holds the draft guard until it is confirmed or refused. */
export function cycleDeleteBusy(gate: CycleDeleteGate): boolean {
  return gate !== null && gate.phase !== 'deleted';
}

/**
 * Settle the draft, then delete. Returns true only for a CONFIRMED delete;
 * a refused one leaves the cycle, and any held draft, as they were.
 */
export async function deleteCycleAfterSettle(opts: {
  id: string;
  gate: {current: CycleDeleteGate};
  flush: () => Promise<boolean>;
  remove: () => Promise<boolean>;
}): Promise<boolean> {
  const {id, gate} = opts;
  gate.current = {id, phase: 'settling'};
  try {
    // A pending edit is saved (so a refused delete loses nothing) and an
    // in-flight save is awaited. A refused flush does not block the delete:
    // its draft is held, and dropped below once the delete is confirmed.
    await opts.flush();
    gate.current = {id, phase: 'deleting'};
    if (!(await opts.remove())) return false;
    gate.current = {id, phase: 'deleted'};
    await opts.flush();
    return true;
  } finally {
    if (gate.current?.phase !== 'deleted') gate.current = null;
  }
}
